/* eslint-disable max-lines */
import { UploadDropzone } from '@bytescale/upload-widget-react';
import { Checkbox, Input, Radio, RadioGroup, Select, SelectItem, Textarea } from '@heroui/react';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';

import {
  ActionBlock, ContentLoadError,
  Flex,
  FormErrorGroup, FormStepper, Grid, inputProps, LightboxGallery,
  PageWrapper,
  Spinner,
} from '../../../../components';
import { podMenuConfig } from '../../../../config';
import { useGetOrganizationsFromFirestore, useQueryHelper } from '../../../../queries';
import { useLangState } from '../../../../state';
import {
  DeviceDocumentFirestoreRequest,
  DeviceDocumentFirestoreResponse,
} from '../../../../types';
import { getLanguagesDictionary } from '../../../../utils/getLanguagesDictionary';
import { optionDeviceCoverUpload } from './config';
import { DeviceEntity, deviceFormOptions } from './CreateDeviceForm.schema';
import { InfoDescription, InfoTitle } from './CreateDeviceForm.styled';
import { MenuDragDropSection } from './MenuDragDropSection';

interface CreateDeviceFormProps {
    onSubmit: (data: DeviceDocumentFirestoreRequest) => void;
    initialValues?: DeviceDocumentFirestoreResponse | null | undefined;
    activeStep: number;
    onStepClick?: (stepNumber: number) => void;
    isEdit?:boolean;
}

const externalAppOptions = [
  { label: 'Museum', value: 'MUSEUM' },
  { label: 'Player', value: 'PLAYER' },
] as const;

export const CreateDeviceForm: FC<CreateDeviceFormProps> = ({
  onSubmit,
  initialValues,
  activeStep,
  isEdit,
}) => {
  const { baseLang, languages } = useLangState();
  const t = getLanguagesDictionary(languages, baseLang);
  const {
    formState,
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<DeviceEntity>({
    ...deviceFormOptions,
    defaultValues: {
      name: initialValues?.name ?? '',
      associated_organization: initialValues?.associated_organization ?? '',
      model: { color: initialValues?.model.color || 'white', size: initialValues?.model.size || '24' },
      homeScreenWelcomeText: { ...initialValues?.homeScreenWelcomeText || t },
      homeScreenImg: initialValues?.homeScreenImg ?? '',
      settings: {
        menuSettings: {
          homeScreen: initialValues?.settings?.menuSettings?.homeScreen ?? ['HOTEL_INFO', 'GOOD_PRACTICES', 'LEAVE_OPINION', 'WIFI', 'SOCIAL_MEDIA', 'REGULATION'],
          slideMenu: initialValues?.settings?.menuSettings?.slideMenu ?? ['HOTEL_INFO', 'GOOD_PRACTICES', 'LEAVE_OPINION', 'WIFI', 'SOCIAL_MEDIA', 'REGULATION', 'SPECIAL_OFFER', 'NEARBY', 'LOYALTY', 'HOTEL_EVENTS', 'RENT', 'GOOD_PRACTICES', 'REGULATION', 'PHONEBOOK', 'VIRTUAL_TOUR'],
          welcomeScreen: initialValues?.settings?.menuSettings?.welcomeScreen ?? ['HOTEL_INFO', 'WIFI', 'LEAVE_OPINION'],
        },
        showSection: {
          foods: initialValues?.settings?.showSection?.foods || false,
          forkids: initialValues?.settings?.showSection?.forkids || false,
          otherplaces: initialValues?.settings?.showSection?.otherplaces || false,
          culture: initialValues?.settings?.showSection?.culture || false,
          trips: initialValues?.settings?.showSection?.trips || false,
        },
      },
      languages,
      externalApp: initialValues?.externalApp || '',
    },
  });

  const organizationQuery = useGetOrganizationsFromFirestore();
  const { data: organization, refetch } = organizationQuery;
  const { isLoading, isError, errorMessage } = useQueryHelper({
    queries: [organizationQuery],
  });

  const homeScreenMenu = watch('settings.menuSettings.homeScreen');
  const slideMenu = watch('settings.menuSettings.slideMenu') || [];
  const welcomeScreenMenu = watch('settings.menuSettings.welcomeScreen');

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={errorMessage} />
      </PageWrapper>
    );
  }

  const cover = watch('homeScreenImg');

  return (
    <>
      <FormStepper
        errors={formState.errors}
        activeStep={activeStep}
        steps={[
          { name: `${!isEdit ? 'Create' : 'Edit'} Device` },
          { name: 'Welcome Screen' },
          { name: 'Sections' },
          { name: 'Menu ' },
          { name: 'Organization ' },
        ]}
      />
      <FormErrorGroup errors={formState.errors} />
      <form
        data-testid="form-create-device"
        noValidate
        id="form-create-device"
        onSubmit={handleSubmit(onSubmit)}
      >
        {activeStep === 1 && (
        <ActionBlock title="About device">
          <Grid
            gridTemplateColumns="3fr 5fr"
            justifyContent="start"
            itemAlign="start"
            gap="25px"
          >
            <Flex gap="15px" direction="column">
              <Input
                {...inputProps}
                {...register('name')}
                placeholder="Enter device name"
                type="text"
                label="Name"
                isInvalid={!!formState.errors.name}
                errorMessage={formState.errors.name?.message}
              />
              <RadioGroup
                onChange={(e) => setValue('model.size', e.target.value)}
                defaultValue={watch('model.size') || initialValues?.model.size || ''}
                label="Model"
                isInvalid={!!formState.errors?.model?.size}
                errorMessage={formState.errors.model?.size?.message}
              >
                <Radio key="14" value="14">14</Radio>
                <Radio key="24" value="21">21</Radio>
                <Radio value="24">24</Radio>
                <Radio value="32">32</Radio>
              </RadioGroup>

              <RadioGroup
                onChange={(e) => setValue('model.color', e.target.value)}
                defaultValue={watch('model.color') || initialValues?.model.color || ''}
                label="Color"
                isInvalid={!!formState.errors?.model?.color}
                errorMessage={formState.errors.model?.color?.message}
              >
                <Radio value="black">Black</Radio>
                <Radio value="white">White</Radio>
              </RadioGroup>
              <Grid
                gridTemplateColumns="1fr"
                justifyContent="start"
                itemAlign="start"
                gap="25px"
              >
                {organization && (
                <Select
                  size="lg"
                  label="External App"
                  variant="bordered"
                  radius="sm"
                  labelPlacement="outside"
                  placeholder="Select external app"
                  {...register('externalApp')}
                  defaultSelectedKeys={[watch('externalApp') || initialValues?.externalApp || '']}
                >
                  {externalAppOptions.map((option) => (
                    <SelectItem key={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </Select>
                )}
              </Grid>
            </Flex>

          </Grid>
        </ActionBlock>
        )}
        {activeStep === 2 && (
        <ActionBlock title="Welcome screen" description="Add Welcome text here, and upload cover photo for welcome screen on this device">
          <Grid
            gridTemplateColumns="3fr 5fr"
            justifyContent="start"
            itemAlign="start"
            gap="25px"
          >
            <Textarea
              className="mt-[20px]"
              {...inputProps}
              {...register('homeScreenWelcomeText.pl')}
              labelPlacement="outside"
              placeholder="Welecome text"
              isInvalid={!!formState.errors.homeScreenWelcomeText?.pl?.message}
              errorMessage={formState.errors.homeScreenWelcomeText?.pl?.message}
              onClear={() => setValue('homeScreenWelcomeText.pl', '')}
            />
            <div>
              { cover === ''
                                    && (
                                    <UploadDropzone
                                      options={optionDeviceCoverUpload}
                                      onComplete={(files) => setValue('homeScreenImg', files[0]?.fileUrl || '')}
                                      width="100%"
                                      height="375px"
                                    />

                                    )}
              {cover && cover !== '' && (
              <LightboxGallery
                title="Cover photo preview"
                onRemove={() => setValue('homeScreenImg', '')}
                draggedCards={[{
                  id: cover,
                  order: 0,
                }]}
              />
              )}
            </div>

          </Grid>
        </ActionBlock>
        )}
        {activeStep === 3 && (
        <ActionBlock title="Sections">
          <Grid
            gridTemplateColumns="3fr 5fr"
            justifyContent="start"
            itemAlign="start"
            gap="25px"
          >
            <Flex direction="column" gap="10px">
              <Checkbox {...register('settings.showSection.forkids')}>For Kids</Checkbox>
              <Checkbox {...register('settings.showSection.foods')}>Food</Checkbox>
              <Checkbox {...register('settings.showSection.trips')}>Trips</Checkbox>
              <Checkbox {...register('settings.showSection.culture')}>Culture</Checkbox>
              <Checkbox {...register('settings.showSection.otherplaces')}>Other</Checkbox>
            </Flex>
          </Grid>
        </ActionBlock>
        )}
        {activeStep === 4 && (
        <ActionBlock title="Menu Configuration">
          <div className="space-y-4">
            <Flex
              itemAlign="flex-start"
              justifyContent="center"
              direction="column"
            >
              <InfoTitle>Customize Your Menu with Ease</InfoTitle>
              <InfoDescription>
                Przeciągaj elementy aby zmienić ich kolejność lub użyj dropdowna aby dodać nowe elementy do menu.
                <br />
                Każde menu możesz skonfigurować niezależnie.
              </InfoDescription>
            </Flex>

            <MenuDragDropSection
              title="Welcome Screen Menu"
              items={welcomeScreenMenu}
              availableItems={podMenuConfig}
              onChange={(newItems) => setValue('settings.menuSettings.welcomeScreen', newItems)}
            />

            <MenuDragDropSection
              title="Home Screen Menu"
              items={homeScreenMenu}
              availableItems={podMenuConfig}
              onChange={(newItems) => setValue('settings.menuSettings.homeScreen', newItems)}
            />

            <MenuDragDropSection
              title="Slide Menu"
              items={slideMenu}
              availableItems={podMenuConfig}
              onChange={(newItems) => setValue('settings.menuSettings.slideMenu', newItems)}
            />
          </div>
        </ActionBlock>
        )}
        {activeStep === 5 && (
        <ActionBlock
          title="Select organization."
          description="Select organization to asign device to this organization"
        >
          <Grid
            gridTemplateColumns="3fr 5fr"
            justifyContent="start"
            itemAlign="start"
            gap="25px"
          >
            {organization && (
            <Select
              size="lg"
              label="Select Organization"
              variant="bordered"
              radius="sm"
              labelPlacement="outside"
              placeholder="Select a organization"
              {...register('associated_organization')}
              defaultSelectedKeys={[watch('associated_organization') || initialValues?.associated_organization || '']}
            >
              {organization.data.map((org) => (
                <SelectItem key={org.id}>{org.companyName}</SelectItem>
              ))}
            </Select>
            )}
          </Grid>
        </ActionBlock>
        )}
      </form>
    </>
  );
};
